import "./MetroMarketsSuggestions.scss";

import React, { ReactElement, useMemo } from "react";
import { FunctionComponent, useEffect } from "react";
import { connect } from "shared/src/stores/storeConnect";
import MetroMarktetsStore, { SearchResultItem, SearchResultState } from "../../../../stores/MetroMarketsStore";
import Slider from "react-slick";
import { jsxWithIntl } from "shared/src/i18n/IntlRootMixin";
import { FormattedMessage } from "react-intl";
import MoneyAmount from "../../lists/MoneyAmount";
import * as _ from "lodash";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import {
    trackCardViewMetroMarketsResult,
    trackOpenMetroMarketsResult
} from "../../../../utils/ZeroSearchResultTrackingUtil";
import { sliderSettings } from "./ZeroResultSettings";

type MetroMarketsSuggestionsProps = {
    query: string;
    results: SearchResultState | undefined | null;
};

const MetroMarketsSuggestions: FunctionComponent<MetroMarketsSuggestionsProps> = ({
    query,
    results
}: MetroMarketsSuggestionsProps): ReactElement | null => {
    const metroMarketsBaseURL = _.get(CountryUtil.getOptionalConfig(), "metroMarketsBaseUrl", null);
    const doSearch = (query: string) => {
        if (metroMarketsBaseURL) {
            MetroMarktetsStore.doSearch(query);
        }
    };
    useEffect(() => doSearch(query), [query]);

    const renderContent = (results: SearchResultState | null | undefined, query: string) => {
        if (results && results.items.length > 0) {
            return (
                <div className="metro-markets-suggestions zero-result-module ">
                    <h3>
                        <FormattedMessage id="SD_ZERO_RESULTS_METRO_MARKETS" />
                    </h3>
                    <p>
                        <FormattedMessage id="SD_ZERO_RESULTS_METRO_MARKETS_HINT" />
                    </p>
                    <Slider {...sliderSettings}>
                        {results.items.map((item: SearchResultItem) => {
                            return <div key={item.id}>{renderMetroMarketsItem(item, query)}</div>;
                        })}
                    </Slider>
                </div>
            );
        } else {
            return null;
        }
    };

    const renderingContent = useMemo(() => renderContent(results, query), [results, query]);

    return renderingContent;
};

const renderMetroMarketsItem = (item: SearchResultItem, query: string) => {
    trackMetroMarketsCardView(item, query);

    const net = (
        <MoneyAmount amount={{ amount: item.bestOffer?.price?.netPrice, currency: item.bestOffer?.price?.currency }} />
    );
    const gross = (
        <MoneyAmount
            amount={{ amount: item.bestOffer?.price?.grossPrice, currency: item.bestOffer?.price?.currency }}
        />
    );
    const metroMarketsBaseURL = _.get(CountryUtil.getOptionalConfig(), "metroMarketsBaseUrl", null);

    return jsxWithIntl(
        <a href={metroMarketsBaseURL + item.id} onClick={() => trackMetroMarketsItemClick(item, query)} target="new">
            <div className="suggestion">
                <div className="picture">
                    <img src={item.image} alt={item.name} />
                </div>
                <p className="name">{item.name}</p>
                <div>
                    <p className="price-net">{net}</p>
                    <p className="price-gross">
                        <FormattedMessage id="PRICE_LABEL_GROSS" />
                        &nbsp;{gross}
                    </p>
                </div>
            </div>
        </a>
    );
};

const trackMetroMarketsCardView = (item: SearchResultItem, query: string) => {
    trackCardViewMetroMarketsResult(query, item.name, item.id);
};

const trackMetroMarketsItemClick = (item: SearchResultItem, query: string) => {
    trackOpenMetroMarketsResult(query, item.name, item.id);
};

const wrapped = connect(
    {
        stores: [
            [
                MetroMarktetsStore,
                (s: typeof MetroMarktetsStore) => ({
                    results: s.getState()
                })
            ]
        ]
    },
    MetroMarketsSuggestions
);

export default wrapped;
